import { useEffect, useState, useCallback, useRef } from "react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PageHeader, EmptyState, ReadOnlyNote } from "@/components/Common";
import { BookOpen, Plus, UploadCloud, FileText, Eye, CheckCircle2, Trash2, Loader2, Pencil, Sparkles, GitCompare, FolderUp, History, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import SOPDiffDialog from "@/components/SOPDiffDialog";

const CATEGORIES = ["Clean Room Prep", "Sterile Compounding", "Non-Sterile", "Hazardous Drugs", "HR & Admin", "Safety", "Other"];
const blank = () => ({ title: "", category: "", version: "1.0", description: "", content: "", file_path: "", original_filename: "" });

export default function SOPs() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [viewer, setViewer] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [aiConverting, setAiConverting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);   // {done, total} while running
  const [bulkDrafts, setBulkDrafts] = useState(null);       // array of {converted, error} when done
  const [diffing, setDiffing] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [versions, setVersions] = useState([]);
  const [delId, setDelId] = useState(null);
  const fileRef = useRef();
  const aiRef = useRef();
  const bulkRef = useRef();
  // Keeps original File handles alive so per-row Retry can rerun a failed
  // conversion without asking the admin to re-browse the whole batch.
  const bulkFilesRef = useRef({});
  const reviseRef = useRef();
  const revisingSop = useRef(null);

  const reload = useCallback(() => api.get("/sops").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);

  const cats = ["All", ...CATEGORIES];
  const shown = filter === "All" ? items : items.filter((s) => s.category === filter);

  const openNew = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...blank(), ...s }); setOpen(true); };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, file_path: data.path, original_filename: data.original_filename, title: f.title || file.name.replace(/\.[^.]+$/, "") }));
      toast.success("PDF attached");
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    if (!form.title || !form.category) return toast.error("Title and category are required");
    try {
      if (editing) await api.put(`/sops/${editing.id}/versioned`, form);
      else await api.post("/sops", form);
      toast.success("SOP saved"); setOpen(false); reload();
    } catch (e) { toast.error("Save failed"); }
  };
  const remove = async () => { try { await api.delete(`/sops/${delId}`); toast.success("Deleted"); setDelId(null); reload(); } catch (e) { toast.error("Delete failed"); } };
  const acknowledge = async (s) => { try { await api.post(`/sops/${s.id}/acknowledge`, {}); toast.success("Acknowledged"); reload(); } catch (e) { toast.error("Failed"); } };

  const onAiFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setAiConverting(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/sops/ai-convert", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing(null);
      setForm({ ...blank(), ...data });
      setOpen(true);
      toast.success("AI reformatted your SOP — review and save");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI conversion failed");
    } finally {
      setAiConverting(false);
      if (aiRef.current) aiRef.current.value = "";
    }
  };

  const runOne = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/sops/ai-convert", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return { file: file.name, converted: data, error: null };
    } catch (err) {
      return { file: file.name, converted: null, error: err.response?.data?.detail || "conversion failed" };
    }
  };

  const onBulkFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    // Cache the actual File objects so per-row Retry can rerun a failed one.
    bulkFilesRef.current = {};
    files.forEach((f) => { bulkFilesRef.current[f.name] = f; });
    setBulkProgress({ done: 0, total: files.length });
    // Run up to 5 conversions in parallel — the backend endpoints are independent,
    // so parallelizing turns a 52-file × 15s serial slog into ~2-3 min instead of ~13 min.
    const CONCURRENCY = 5;
    const results = new Array(files.length);
    let cursor = 0, done = 0;
    const worker = async () => {
      while (cursor < files.length) {
        const idx = cursor++;
        results[idx] = await runOne(files[idx]);
        done++;
        setBulkProgress({ done, total: files.length });
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, files.length) }, worker));
    setBulkProgress(null);
    setBulkDrafts(results);
    if (bulkRef.current) bulkRef.current.value = "";
    const ok = results.filter((r) => !r.error).length;
    toast.success(`Processed ${files.length} · ${ok} ready, ${files.length - ok} failed`);
  };

  const saveBulkDraft = async (draft) => {
    try {
      await api.post("/sops", draft.converted);
      setBulkDrafts((arr) => arr.map((r) => r.file === draft.file ? { ...r, saved: true } : r));
      toast.success(`Saved "${draft.converted.title}"`);
      reload();
    } catch (e) { toast.error("Save failed"); }
  };

  // Kept on the closure so the Bulk Drafts dialog can call it too — a per-row
  // Retry button lets the user re-run a single failed conversion without
  // re-picking the whole 52-file selection.
  const retryBulkDraft = async (draft, idx) => {
    const inputFile = bulkFilesRef.current?.[draft.file];
    if (!inputFile) { toast.error("Original file no longer in memory — re-pick and try again."); return; }
    setBulkDrafts((arr) => arr.map((r, i) => i === idx ? { ...r, retrying: true, error: null } : r));
    const r = await runOne(inputFile);
    setBulkDrafts((arr) => arr.map((row, i) => i === idx ? { ...r, retrying: false } : row));
  };

  const onReviseFile = async (e) => {
    const file = e.target.files?.[0]; if (!file || !revisingSop.current) return;
    setAiConverting(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/sops/ai-convert", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDiffing({ oldSop: revisingSop.current, newSop: { ...revisingSop.current, ...data } });
    } catch (err) { toast.error(err.response?.data?.detail || "AI conversion failed"); }
    finally { setAiConverting(false); if (reviseRef.current) reviseRef.current.value = ""; }
  };

  const acceptDiff = async (newSop) => {
    try {
      const version = (() => {
        const v = parseFloat(newSop.version || "1.0");
        return isNaN(v) ? "1.0" : (v + 0.1).toFixed(1);
      })();
      await api.put(`/sops/${diffing.oldSop.id}/versioned`, { ...newSop, version });
      toast.success(`Replaced — bumped to v${version}`);
      setDiffing(null); reload();
    } catch (e) { toast.error("Save failed"); }
  };

  const openHistory = async (sop) => {
    setHistoryFor(sop);
    try {
      const { data } = await api.get(`/sops/${sop.id}/history`);
      setVersions(data.versions || []);
    } catch { toast.error("Could not load history"); }
  };
  const rollback = async (vid) => {
    if (!historyFor) return;
    try {
      await api.post(`/sops/${historyFor.id}/rollback/${vid}`);
      toast.success("Rolled back");
      setHistoryFor(null); setVersions([]); reload();
    } catch { toast.error("Rollback failed"); }
  };

  return (
    <div>
      <PageHeader testid="sops-header" title="SOP Catalog" subtitle="Upload PDFs, author digital SOPs, or drop your existing SOPs and let AI reformat them into a uniform layout.">
        {isAdmin ? (
          <div className="flex gap-2 flex-wrap">
            <input ref={aiRef} type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*" className="hidden" onChange={onAiFile} data-testid="sop-ai-file" />
            <input ref={bulkRef} type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*" multiple className="hidden" onChange={onBulkFiles} data-testid="sop-bulk-file" />
            <input ref={reviseRef} type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*" className="hidden" onChange={onReviseFile} data-testid="sop-revise-file" />
            <Button data-testid="sop-ai-convert-button" onClick={() => aiRef.current?.click()} disabled={aiConverting || !!bulkProgress} variant="outline" className="gap-1.5 border-pink-300 text-pink-800 hover:bg-pink-50">
              {aiConverting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} AI Convert
            </Button>
            <Button data-testid="sop-bulk-convert-button" onClick={() => bulkRef.current?.click()} disabled={aiConverting || !!bulkProgress} variant="outline" className="gap-1.5 border-pink-300 text-pink-800 hover:bg-pink-50">
              {bulkProgress ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderUp className="w-4 h-4" />}
              {bulkProgress ? `${bulkProgress.done}/${bulkProgress.total}` : "Bulk Convert"}
            </Button>
            <Button data-testid="sop-add-button" onClick={openNew} className="bg-pink-700 hover:bg-pink-800 gap-1.5"><Plus className="w-4 h-4" /> New SOP</Button>
          </div>
        ) : <ReadOnlyNote />}
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-5">
        {cats.map((c) => (
          <button key={c} data-testid={`sop-filter-${c.replace(/\s|&/g, "-")}`} onClick={() => setFilter(c)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === c ? "bg-pink-700 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-pink-300"}`}>{c}</button>
        ))}
      </div>

      {shown.length === 0 ? <Card className="border-slate-200"><EmptyState icon={BookOpen} title="No SOPs in this category" hint="Create or upload a SOP to build your catalog." /></Card> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {shown.map((s) => (
            <Card key={s.id} data-testid={`sop-card-${s.id}`} className="p-5 border-slate-200 hover:shadow-md transition-shadow flex flex-col">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg bg-pink-50 flex items-center justify-center"><FileText className="w-5 h-5 text-pink-700" /></div>
                <span className="text-[11px] font-mono text-slate-400">v{s.version}</span>
              </div>
              <div className="text-[11px] font-medium text-pink-800 mt-3">{s.category}</div>
              <h3 className="font-heading font-semibold text-slate-800 mt-1 leading-snug">{s.title}</h3>
              <p className="text-sm text-slate-500 mt-1 line-clamp-2 flex-1">{s.description}</p>
              <div className="text-xs text-slate-400 mt-3">{(s.acknowledgments || []).length} acknowledgment(s)</div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                <Button data-testid={`sop-view-${s.id}`} size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => setViewer(s)}><Eye className="w-3.5 h-3.5" /> View</Button>
                <Button data-testid={`sop-ack-${s.id}`} size="sm" variant="ghost" className="gap-1.5 h-8 text-emerald-600" onClick={() => acknowledge(s)}><CheckCircle2 className="w-3.5 h-3.5" /> Sign off</Button>
                {isAdmin && <div className="ml-auto flex">
                  <Button data-testid={`sop-history-${s.id}`} size="icon" variant="ghost" className="h-8 w-8" title="Version history" onClick={() => openHistory(s)}><History className="w-4 h-4 text-slate-400 hover:text-pink-700" /></Button>
                  <Button data-testid={`sop-revise-${s.id}`} size="icon" variant="ghost" className="h-8 w-8" title="Re-upload & compare"
                    onClick={() => { revisingSop.current = s; reviseRef.current?.click(); }}><GitCompare className="w-4 h-4 text-pink-700" /></Button>
                  <Button data-testid={`sop-edit-${s.id}`} size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(s)}><Pencil className="w-4 h-4 text-slate-400" /></Button>
                  <Button data-testid={`sop-delete-${s.id}`} size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDelId(s.id)}><Trash2 className="w-4 h-4 text-rose-400" /></Button>
                </div>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Editor */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="sop-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2">
            {editing ? "Edit" : "New"} SOP
            {!editing && form.content && form.content.includes("PURPOSE") && (
              <span className="text-xs font-normal bg-pink-50 text-pink-800 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI draft — verify below</span>
            )}
          </DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2"><Label className="text-xs text-slate-600">Title *</Label><Input data-testid="sop-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label className="text-xs text-slate-600">Version</Label><Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} className="font-mono" /></div>
            <div className="sm:col-span-3"><Label className="text-xs text-slate-600">Category *</Label>
              <select data-testid="sop-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
                <option value="">Select…</option>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            <div className="sm:col-span-3"><Label className="text-xs text-slate-600">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <div className="sm:col-span-3"><Label className="text-xs text-slate-600">Digital Content (type or paste the SOP body)</Label><Textarea data-testid="sop-content-input" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={12} className="font-mono text-xs" /></div>
            <div className="sm:col-span-3">
              <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={onFile} />
              <Button data-testid="sop-upload-pdf-button" type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-1.5">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} {form.file_path ? "Replace PDF" : "Attach PDF"}
              </Button>
              {form.original_filename && <span className="ml-3 text-xs text-slate-500 font-mono">{form.original_filename}</span>}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="sop-save-button" onClick={save} className="bg-pink-700 hover:bg-pink-800">Save SOP</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reader */}
      <Dialog open={!!viewer} onOpenChange={() => setViewer(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="sop-viewer">
          <DialogHeader><DialogTitle className="font-heading">{viewer?.title}</DialogTitle></DialogHeader>
          {viewer && (
            <div>
              <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                <span className="bg-pink-50 text-pink-800 px-2 py-0.5 rounded-full">{viewer.category}</span>
                <span className="font-mono">v{viewer.version}</span>
              </div>
              {viewer.description && <p className="text-sm text-slate-600 mb-4">{viewer.description}</p>}
              {(() => {
                // Only iframe things a browser can actually render inline.
                // AI-converted .docx files still have `file_path` set (we archive
                // the original), but the iframe renders blank for Word docs — so
                // prefer the reformatted `content` when file_path isn't a PDF/image.
                const isViewable = (viewer.file_path || "").toLowerCase().match(/\.(pdf|png|jpe?g|webp|gif|svg)$/);
                if (isViewable) {
                  return <iframe title="sop-pdf" src={fileUrl(viewer.file_path)} className="w-full h-[60vh] border border-slate-200 rounded-lg" />;
                }
                if (viewer.content) {
                  return <pre className="whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-4 font-sans">{viewer.content}</pre>;
                }
                if (viewer.file_path) {
                  return (
                    <div className="p-4 border border-slate-200 rounded-lg text-sm text-slate-600">
                      Original file attached ({viewer.original_filename || "download"}). Preview isn't available in the browser for this format — <a href={fileUrl(viewer.file_path)} target="_blank" rel="noreferrer" className="text-pink-700 underline">download the original</a> or open Edit to view the reformatted text.
                    </div>
                  );
                }
                return <p className="text-sm text-slate-400">No content attached.</p>;
              })()}
              {(viewer.acknowledgments || []).length > 0 && (
                <div className="mt-4 text-xs text-slate-500">
                  <div className="font-semibold mb-1">Acknowledgments</div>
                  {viewer.acknowledgments.map((a) => <div key={`${a.employee}-${a.at}`}>✓ {a.employee} — {new Date(a.at).toLocaleString()}</div>)}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this SOP?</AlertDialogTitle>
            <AlertDialogDescription>This removes it from your catalog. This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="sop-confirm-delete" onClick={remove} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SOPDiffDialog open={!!diffing} onClose={() => setDiffing(null)} oldSop={diffing?.oldSop} newSop={diffing?.newSop} onAccept={acceptDiff} />

      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="sop-history-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><History className="w-5 h-5 text-pink-700" /> Version History — {historyFor?.title}</DialogTitle></DialogHeader>
          {versions.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No prior versions saved yet.</p>
          ) : (
            <div className="space-y-2">
              {versions.map((v) => (
                <div key={v.id} data-testid={`sop-version-${v.id}`} className="border border-slate-200 rounded-lg p-3 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-slate-800">v{v.version}</span>
                      <span className="text-xs text-slate-400">{new Date(v.snapshotted_at).toLocaleString()}</span>
                    </div>
                    {v.snapshotted_by && <div className="text-xs text-slate-500 mt-0.5">by {v.snapshotted_by}</div>}
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{v.description || v.note}</p>
                  </div>
                  <Button data-testid={`sop-rollback-${v.id}`} size="sm" variant="outline" onClick={() => rollback(v.id)} className="gap-1.5 border-pink-300 text-pink-800 hover:bg-pink-50"><RotateCcw className="w-3.5 h-3.5" /> Restore</Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!bulkDrafts} onOpenChange={(o) => !o && setBulkDrafts(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="bulk-drafts-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><FolderUp className="w-5 h-5 text-pink-700" /> Bulk AI Convert — {bulkDrafts?.length || 0} file(s)</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {(bulkDrafts || []).map((d, i) => (
              <div key={d.file || `draft-${i}`} data-testid={`bulk-draft-${i}`} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-slate-400 font-mono truncate">{d.file}</div>
                    {d.error ? (
                      <div className="text-sm text-rose-700 mt-1">{d.error}</div>
                    ) : d.retrying ? (
                      <div className="text-sm text-slate-500 mt-1 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Retrying…</div>
                    ) : (
                      <>
                        <div className="font-heading font-semibold text-slate-800 truncate">{d.converted.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{d.converted.category} · v{d.converted.version}</div>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{d.converted.description}</p>
                      </>
                    )}
                  </div>
                  {d.error && !d.retrying && (
                    <Button data-testid={`bulk-retry-${i}`} size="sm" variant="outline"
                      onClick={() => retryBulkDraft(d, i)}
                      className="border-amber-300 text-amber-800 hover:bg-amber-50 h-8">Retry</Button>
                  )}
                  {!d.error && !d.retrying && (
                    d.saved
                      ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> Saved</span>
                      : <Button data-testid={`bulk-save-${i}`} size="sm" onClick={() => saveBulkDraft(d)} className="bg-pink-700 hover:bg-pink-800 h-8">Save</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setBulkDrafts(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
